// Finds a hallucination-risk quote in rendered text and highlights the sentence around it (CSS Custom
// Highlight API, with the browser selection as a fallback), then scrolls it into view.
const SENTENCE_HIGHLIGHT = 'hc-ai-risk-sentence';
const QUOTE_HIGHLIGHT = 'hc-ai-risk-quote';
// A run-on transcript "sentence" is cut to this many characters on each side of the quote.
const MAX_SENTENCE_REACH = 400;

const supportsHighlights = () => typeof CSS !== 'undefined' && CSS.highlights && typeof window.Highlight !== 'undefined';

export function clearPassageHighlight() {
    if (supportsHighlights()) {
        CSS.highlights.delete(SENTENCE_HIGHLIGHT);
        CSS.highlights.delete(QUOTE_HIGHLIGHT);
    }
}

// The text nodes under `root` joined into one string, so a quote that spans formatting tags still matches.
function collectText(root) {
    const nodes = [];
    let full = '';
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        nodes.push({ node, start: full.length });
        full += node.nodeValue;
    }
    return { nodes, full };
}

function toRange(nodes, start, end) {
    const locate = (offset, isEnd) => {
        for (let i = nodes.length - 1; i >= 0; i -= 1) {
            if (nodes[i].start < offset || (!isEnd && nodes[i].start === offset) || (isEnd && nodes[i].start <= offset)) {
                return { node: nodes[i].node, offset: Math.min(offset - nodes[i].start, nodes[i].node.nodeValue.length) };
            }
        }
        return { node: nodes[0].node, offset: 0 };
    };
    const from = locate(start, false);
    const to = locate(end, true);
    const range = document.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset);
    return range;
}

export function highlightPassage(root, quote) {
    if (!root || !quote) return false;
    const { nodes, full } = collectText(root);
    if (!nodes.length) return false;

    // The model's quote may differ from the text in whitespace/line breaks and case.
    const pattern = quote.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
    const match = new RegExp(pattern, 'i').exec(full);
    if (!match) return false;
    const quoteStart = match.index;
    const quoteEnd = match.index + match[0].length;

    let sentenceStart = quoteStart;
    while (sentenceStart > 0 && quoteStart - sentenceStart < MAX_SENTENCE_REACH && !/[.!?\n]/.test(full[sentenceStart - 1])) sentenceStart -= 1;
    while (sentenceStart < quoteStart && /\s/.test(full[sentenceStart])) sentenceStart += 1;
    let sentenceEnd = quoteEnd;
    while (sentenceEnd < full.length && sentenceEnd - quoteEnd < MAX_SENTENCE_REACH && !/[.!?\n]/.test(full[sentenceEnd])) sentenceEnd += 1;
    if (sentenceEnd < full.length && full[sentenceEnd] !== '\n') sentenceEnd += 1;

    const sentence = toRange(nodes, sentenceStart, sentenceEnd);
    const quoteRange = toRange(nodes, quoteStart, quoteEnd);
    if (supportsHighlights()) {
        window.getSelection()?.removeAllRanges();
        CSS.highlights.set(SENTENCE_HIGHLIGHT, new window.Highlight(sentence));
        CSS.highlights.set(QUOTE_HIGHLIGHT, new window.Highlight(quoteRange));
    } else {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(sentence);
    }

    // Formatted text: its paragraph scrolls into view. Plain text is one text node directly under the
    // scrolling box, so the box is scrolled to the sentence's own position instead.
    const block = sentence.startContainer.parentElement;
    if (block && block !== root) {
        block.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } else {
        const top = sentence.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
        root.scrollTo({ top: Math.max(0, top - root.clientHeight / 3), behavior: 'smooth' });
    }
    return true;
}
